import { z } from 'zod';
import { Kuerzel } from './auth';
import {
  GRUENDE_MIT_FOTO,
  RETOUREN_GRUND_CODES,
  type AusschlussGrund,
  type CarrierStatus,
  type LabelStatus,
  type PaketZielTyp,
  type RetourenCarrier,
  type RetourenGrund,
  type RetourenPositionStatus,
  type RetoureStatus,
} from './retouren';

/**
 * Retouren D3: Kundenportal und E-Mails (Briefing Teil D, Abschnitte 5, 12 und 13).
 * Schnittstelle /v1/kunde/* – dieselbe für das Portal des Marktplatzes und später die Shopify-Kundenkonto-Erweiterung.
 */

/* ------------------------------------------------------------------ Sprache */

export const KUNDEN_SPRACHEN = { de: 'Deutsch', en: 'English' } as const;
export type KundenSprache = keyof typeof KUNDEN_SPRACHEN;
export const KUNDEN_SPRACHE_CODES = Object.keys(KUNDEN_SPRACHEN) as [KundenSprache, ...KundenSprache[]];

/** Sprache aus dem Shop (customerLocale „de“, „de-AT“, „en-GB“ …) – alles außer Deutsch wird Englisch, leer bleibt Deutsch */
export function spracheAus(locale: string | null | undefined): KundenSprache {
  const l = String(locale ?? '').trim().toLowerCase();
  return !l || l.startsWith('de') ? 'de' : 'en';
}

/** Gründe für Kunden auf Englisch (deutsch: RETOUREN_GRUENDE) */
export const RETOUREN_GRUENDE_EN: Record<RetourenGrund, string> = {
  zu_gross: 'Too big',
  zu_klein: 'Too small',
  gefaellt_nicht: "Don't like it",
  anders_als_abgebildet: 'Different from the picture',
  qualitaet: 'Quality not as expected',
  falscher_artikel_erhalten: 'Received the wrong item',
  defekt: 'Item is defective',
  zu_spaet: 'Arrived too late',
  doppelt_bestellt: 'Ordered twice',
  sonstiges: 'Other',
};

/* ------------------------------------------------------------------ Fotos */

export const RETOUREN_FOTO_TYPEN = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;
export const RETOUREN_FOTO_MAX_BYTES = 10 * 1024 * 1024;
export const RETOUREN_FOTO_MAX_ANZAHL = 5;

/* ------------------------------------------------------------------ Eingaben */

/** Gastzugang: Bestellnummer und E-Mail-Adresse oder PLZ der Lieferanschrift */
export const KundeZugangSchema = z
  .object({
    marktplatz: Kuerzel,
    bestellnummer: z.string().trim().min(1, 'Bitte die Bestellnummer angeben').max(40),
    email: z.string().trim().toLowerCase().max(190).optional(),
    plz: z.string().trim().max(20).optional(),
  })
  .refine((z) => !!z.email || !!z.plz, { message: 'Bitte E-Mail-Adresse oder Postleitzahl angeben', path: ['email'] });
export type KundeZugangEingabe = z.input<typeof KundeZugangSchema>;

export interface KundeZugang {
  /** kurzlebiger Schlüssel für die weiteren Aufrufe (Authorization: Bearer …) */
  token: string;
  bestellungId: string;
  gueltigBis: string;
}

export const KundeAuswahlSchema = z
  .object({
    art: z.enum(['haendler', 'eigen']),
    positionId: z.string().uuid(),
    /** Anzahl Stücke dieser Position mit demselben Grund */
    menge: z.number().int().min(1).max(999).default(1),
    grund: z.enum(RETOUREN_GRUND_CODES),
    grundText: z.string().trim().max(1000).default(''),
    fotos: z.array(z.string().uuid()).max(RETOUREN_FOTO_MAX_ANZAHL).default([]),
  })
  .superRefine((a, ctx) => {
    if ((a.grund === 'sonstiges' || GRUENDE_MIT_FOTO.includes(a.grund)) && a.grundText.length < 3)
      ctx.addIssue({ code: 'custom', path: ['grundText'], message: 'Bitte kurz beschreiben' });
  });
export type KundeAuswahl = z.input<typeof KundeAuswahlSchema>;

export const KundeVorschauSchema = z.object({ auswahl: z.array(KundeAuswahlSchema).min(1, 'Bitte mindestens einen Artikel auswählen').max(100) });

export const RUECKGABE_ARTEN = { qr: 'QR-Code (papierlos)', pdf: 'Label zum Ausdrucken' } as const;
export type RueckgabeArt = keyof typeof RUECKGABE_ARTEN;

export const KundeRetoureSchema = KundeVorschauSchema.extend({
  bestellungId: z.string().uuid(),
  /** Pflicht, wenn der Shop keine E-Mail-Adresse geliefert hat */
  email: z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190).optional(),
  sprache: z.enum(KUNDEN_SPRACHE_CODES).default('de'),
  rueckgabeart: z.enum(['qr', 'pdf']).default('qr'),
});
export type KundeRetoureEingabe = z.input<typeof KundeRetoureSchema>;

/* ------------------------------------------------------------------ Antworten */

/** Warum ein Artikel (noch) nicht zurückgegeben werden kann */
export const RUECKGABE_SPERREN = {
  frist: 'Rückgabefrist abgelaufen',
  ausschluss: 'Von der Rückgabe ausgeschlossen',
  zurueckgegeben: 'Bereits zurückgegeben',
  nicht_versendet: 'Noch nicht versendet',
  storniert: 'Storniert',
} as const;
export type RueckgabeSperre = keyof typeof RUECKGABE_SPERREN;

export interface RueckgabePosition {
  art: 'haendler' | 'eigen';
  id: string;
  titel: string;
  variante: string;
  bild: string | null;
  /** „Verkauf und Versand durch …“ – Händler oder Marktplatz */
  verkaeufer: string;
  menge: number;
  /** bezahlter Preis je Stück in Cent (nach Rabatt) */
  preis: number;
  /** Zustellung, sonst Versand plus 2 Werktage (geschaetzt) */
  lieferdatum: string | null;
  lieferdatumGeschaetzt: boolean;
  /** Letzter Tag der Rückgabe (Ende des Tages, Europe/Berlin) */
  rueckgabeBis: string | null;
  /** so viele Stücke können noch zurückgegeben werden */
  frei: number;
  /** bereits angemeldete oder zurückgegebene Stücke */
  zurueckgegeben: number;
  sperre: RueckgabeSperre | null;
  ausschlussGrund: AusschlussGrund | null;
}

export interface KundenRetoureKurz {
  id: string;
  nummer: string;
  status: RetoureStatus;
  angemeldetAm: string;
  pakete: number;
}

export interface RueckgabeAnsicht {
  bestellung: { id: string; nr: string; bestelltAm: string; waehrung: string };
  marktplatz: string;
  positionen: RueckgabePosition[];
  richtlinie: { fristTage: number; kostenlos: boolean; kostenKunde: number };
  /** false: der Kunde gibt beim Absenden eine E-Mail-Adresse an */
  emailBekannt: boolean;
  /** maskiert, z. B. „er…@beispiel.de“ */
  email: string;
  sprache: KundenSprache;
  retouren: KundenRetoureKurz[];
}

export interface PaketVorschau {
  nr: number;
  zielTyp: PaketZielTyp;
  /** Händler, Marktplatz oder Retourenzentrum */
  empfaenger: string;
  carrier: RetourenCarrier;
  sperrgut: boolean;
  positionen: { titel: string; anzahl: number }[];
}

export interface ErstattungVorschau {
  waehrung: string;
  ware: number;
  versand: number;
  abzug: number;
  gesamt: number;
}

export interface RueckgabeVorschau {
  pakete: PaketVorschau[];
  erstattung: ErstattungVorschau;
}

/** Schritte im Zeitstrahl je Paket */
export const ZEITSTRAHL_SCHRITTE = ['angemeldet', 'abgegeben', 'unterwegs', 'angekommen', 'geprueft', 'erstattet'] as const;
export type ZeitstrahlSchritt = (typeof ZEITSTRAHL_SCHRITTE)[number];

export interface KundenPaket {
  id: string;
  nr: number;
  zielTyp: PaketZielTyp;
  empfaenger: string;
  /** Rücksendeadresse – für Pakete ohne Label (Ausland, Abholung) */
  adresse: { name: string; strasse: string; plz: string; ort: string; land: string } | null;
  carrier: RetourenCarrier;
  labelStatus: LabelStatus;
  carrierStatus: CarrierStatus;
  sendungsnummer: string;
  sendungsverfolgung: string | null;
  labelGueltigBis: string | null;
  /** Download-Adressen (mit Schlüssel); null = (noch) nicht vorhanden */
  label: string | null;
  qrCode: string | null;
  ruecksendeschein: string | null;
  stornierbar: boolean;
  storniert: boolean;
  positionen: { id: string; titel: string; grund: RetourenGrund; status: RetourenPositionStatus }[];
  zeitstrahl: { schritt: ZeitstrahlSchritt; am: string | null; erreicht: boolean }[];
}

export interface KundenRetoure {
  id: string;
  nummer: string;
  status: RetoureStatus;
  angemeldetAm: string;
  bestellung: { id: string; nr: string };
  marktplatz: string;
  sprache: KundenSprache;
  pakete: KundenPaket[];
  erstattung: ErstattungVorschau & { erstattet: number; erstattetAm: string | null };
  /** Schlüssel des persönlichen Retouren-Links – nur direkt nach dem Anmelden */
  link?: string;
}

/* ------------------------------------------------------------------ E-Mails */

export interface RetourenMailArt {
  titel: string;
  empfaenger: 'kunde' | 'haendler';
  /** false: die Vorlage ist schon pflegbar, verschickt wird sie mit einer späteren Ausbaustufe */
  inBetrieb: boolean;
  /** Platzhalter, die im Betreff und in der Einleitung erlaubt sind */
  platzhalter: readonly string[];
  standard: Partial<Record<KundenSprache, { betreff: string; einleitung: string }>>;
}

const PLATZHALTER_KUNDE = ['{nummer}', '{bestellung}', '{marktplatz}'] as const;
const PLATZHALTER_HAENDLER = ['{nummer}', '{bestellung}', '{marktplatz}', '{haendler}'] as const;

export const RETOUREN_MAIL_ARTEN = {
  retoure_angemeldet: {
    titel: 'Kunde: Rücksendung angemeldet',
    empfaenger: 'kunde',
    inBetrieb: true,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Ihre Rücksendung {nummer}', einleitung: 'vielen Dank, Ihre Rücksendung zur Bestellung {bestellung} ist angemeldet. Bitte packen Sie jedes Paket getrennt – jedes hat ein eigenes Label.' },
      en: { betreff: 'Your return {nummer}', einleitung: 'thank you, your return for order {bestellung} has been registered. Please pack each parcel separately – each one has its own label.' },
    },
  },
  retoure_label: {
    titel: 'Kunde: Rücksendelabel nachgereicht',
    empfaenger: 'kunde',
    inBetrieb: true,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Ihr Rücksendelabel zu {nummer}', einleitung: 'das Rücksendelabel für Ihre Rücksendung {nummer} liegt jetzt vor.' },
      en: { betreff: 'Your return label for {nummer}', einleitung: 'the return label for your return {nummer} is now ready.' },
    },
  },
  retoure_erinnerung: {
    titel: 'Kunde: Erinnerung, Paket noch nicht abgegeben',
    empfaenger: 'kunde',
    inBetrieb: true,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Erinnerung: Ihre Rücksendung {nummer}', einleitung: 'Ihre Rücksendung {nummer} ist angemeldet, aber noch nicht abgegeben. Bitte denken Sie an die Rückgabefrist.' },
      en: { betreff: 'Reminder: your return {nummer}', einleitung: "your return {nummer} is registered but hasn't been dropped off yet. Please keep the return deadline in mind." },
    },
  },
  retoure_storniert: {
    titel: 'Kunde: Rücksendung zurückgezogen',
    empfaenger: 'kunde',
    inBetrieb: true,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Rücksendung {nummer} zurückgezogen', einleitung: 'Sie haben die Anmeldung für ein Paket Ihrer Rücksendung {nummer} zurückgezogen. Das Label dieses Pakets ist damit ungültig.' },
      en: { betreff: 'Return {nummer} cancelled', einleitung: 'you have cancelled a parcel of your return {nummer}. Its label is no longer valid.' },
    },
  },
  retoure_angekommen: {
    titel: 'Kunde: Paket angekommen',
    empfaenger: 'kunde',
    inBetrieb: true,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Ihr Paket zu {nummer} ist angekommen', einleitung: 'ein Paket Ihrer Rücksendung {nummer} ist angekommen und wird jetzt geprüft.' },
      en: { betreff: 'Your parcel for {nummer} has arrived', einleitung: 'a parcel of your return {nummer} has arrived and is now being checked.' },
    },
  },
  retoure_erstattet: {
    titel: 'Kunde: Erstattung veranlasst',
    empfaenger: 'kunde',
    inBetrieb: false,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Ihre Erstattung zu {nummer}', einleitung: 'wir haben die Erstattung für Ihre Rücksendung {nummer} veranlasst.' },
      en: { betreff: 'Your refund for {nummer}', einleitung: 'we have issued the refund for your return {nummer}.' },
    },
  },
  retoure_klaerfall: {
    titel: 'Kunde: Ergebnis der Prüfung',
    empfaenger: 'kunde',
    inBetrieb: false,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Ergebnis der Prüfung zu {nummer}', einleitung: 'wir haben Ihre Rücksendung {nummer} geprüft.' },
      en: { betreff: 'Result of the inspection for {nummer}', einleitung: 'we have inspected your return {nummer}.' },
    },
  },
  retoure_abholung: {
    titel: 'Kunde: Abholtermin (Sperrgut)',
    empfaenger: 'kunde',
    inBetrieb: true,
    platzhalter: PLATZHALTER_KUNDE,
    standard: {
      de: { betreff: 'Abholung Ihrer Rücksendung {nummer}', einleitung: 'für Ihre Rücksendung {nummer} ist die Abholung beauftragt.' },
      en: { betreff: 'Collection of your return {nummer}', einleitung: 'the collection of your return {nummer} has been arranged.' },
    },
  },
  retoure_haendler_neu: {
    titel: 'Händler: neue Retoure',
    empfaenger: 'haendler',
    inBetrieb: true,
    platzhalter: PLATZHALTER_HAENDLER,
    standard: {
      de: { betreff: 'Neue Retoure {nummer} zur Bestellung {bestellung}', einleitung: 'für die Bestellung {bestellung} hat ein Kunde eine Rücksendung an Sie angemeldet.' },
    },
  },
  retoure_haendler_label: {
    titel: 'Händler: Rücksendelabel bereitstellen',
    empfaenger: 'haendler',
    inBetrieb: true,
    platzhalter: PLATZHALTER_HAENDLER,
    standard: {
      de: { betreff: 'Bitte Rücksendelabel bereitstellen – {nummer}', einleitung: 'für die Retoure {nummer} brauchen wir ein Rücksendelabel von Ihnen. Bitte laden Sie es im Händlerportal unter Retouren hoch.' },
    },
  },
  retoure_haendler_label_erinnerung: {
    titel: 'Händler: Erinnerung Rücksendelabel',
    empfaenger: 'haendler',
    inBetrieb: true,
    platzhalter: PLATZHALTER_HAENDLER,
    standard: {
      de: { betreff: 'Erinnerung: Rücksendelabel für {nummer} fehlt', einleitung: 'die Frist für das Rücksendelabel der Retoure {nummer} ist abgelaufen. Ohne Label innerhalb von 24 Stunden nach der Anforderung erzeugt der Marktplatz ein Ersatz-Label auf Ihre Kosten.' },
    },
  },
  retoure_haendler_zugestellt: {
    titel: 'Händler: Paket zugestellt – bitte buchen',
    empfaenger: 'haendler',
    inBetrieb: true,
    platzhalter: PLATZHALTER_HAENDLER,
    standard: {
      de: { betreff: 'Retoure {nummer} zugestellt – bitte buchen', einleitung: 'das Paket der Retoure {nummer} ist bei Ihnen angekommen. Bitte prüfen Sie die Ware und buchen Sie den Eingang im Händlerportal unter Retouren.' },
    },
  },
  retoure_haendler_frist: {
    titel: 'Händler: Frist läuft ab',
    empfaenger: 'haendler',
    inBetrieb: true,
    platzhalter: PLATZHALTER_HAENDLER,
    standard: {
      de: { betreff: 'Frist läuft ab: Retoure {nummer}', einleitung: 'die Frist zur Buchung der Retoure {nummer} endet bald. Ohne Buchung gilt die Ware danach als angenommen; der Kunde wird erstattet und Ihr Konto belastet.' },
    },
  },
} as const satisfies Record<string, RetourenMailArt>;
export type RetourenMailArtCode = keyof typeof RETOUREN_MAIL_ARTEN;
export const RETOUREN_MAIL_ART_CODES = Object.keys(RETOUREN_MAIL_ARTEN) as [RetourenMailArtCode, ...RetourenMailArtCode[]];

/** Sprachen, in denen eine Mail-Art verschickt wird (Händler-Mails nur Deutsch) */
export const mailSprachen = (art: RetourenMailArtCode): KundenSprache[] => Object.keys(RETOUREN_MAIL_ARTEN[art].standard) as KundenSprache[];

/** Standardtext einer Mail-Art; Händler-Mails gibt es nur auf Deutsch */
export function mailStandard(art: RetourenMailArtCode, sprache: KundenSprache): { betreff: string; einleitung: string } {
  const st: RetourenMailArt['standard'] = RETOUREN_MAIL_ARTEN[art].standard;
  return st[sprache] ?? st.de!;
}

/** Platzhalter einsetzen; unbekannte bleiben stehen */
export function platzhalterEinsetzen(text: string, werte: Partial<Record<string, string>>): string {
  return text.replace(/\{([a-z]+)\}/g, (ganz, k: string) => werte[k] ?? ganz);
}

export const RetourenMailVorlageSchema = z
  .object({
    betreff: z.string().trim().min(3, 'Bitte einen Betreff angeben').max(200, 'Höchstens 200 Zeichen'),
    einleitung: z.string().trim().min(3, 'Bitte eine Einleitung angeben').max(2000, 'Höchstens 2.000 Zeichen'),
  });
export type RetourenMailVorlageEingabe = z.infer<typeof RetourenMailVorlageSchema>;

export interface RetourenMailVorlage {
  art: RetourenMailArtCode;
  sprache: KundenSprache;
  titel: string;
  empfaenger: 'kunde' | 'haendler';
  inBetrieb: boolean;
  platzhalter: readonly string[];
  betreff: string;
  einleitung: string;
  /** true: es gilt der Standardtext (nichts gespeichert) */
  standard: boolean;
  geaendertAm: string | null;
}

/** Vorschau und Test-Mail mit dem Stand im Editor (noch nicht gespeichert) */
export const RetourenMailVorschauSchema = RetourenMailVorlageSchema;
export const RetourenMailTestSchema = RetourenMailVorlageSchema.extend({ an: z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190) });

/** Antwort von GET /api/konto/retouren/kundenportal */
export interface RetourenKundenportal {
  /** Gastzugang des Portals */
  portalUrl: string;
  /** Liquid für die Benachrichtigungen des Shops (Versand- und Bestellbestätigung) */
  liquid: { de: string; en: string };
  /** Stand des persönlichen Links in den Shop-Bestellungen der letzten 120 Tage (offen höchstens 10.000 gezählt) */
  links: { offen: number; zuletzt: string | null; fehler: string | null; shopVerbunden: boolean; tage: number; zaehlenBis: number };
  vorlagen: RetourenMailVorlage[];
}

/** Persönlicher Link als Metafeld der Shop-Bestellung (Namespace und Schlüssel) */
export const RETOUREN_LINK_METAFELD = { namespace: 'dxl_marketplace', key: 'retouren_link' } as const;

export function liquidBaustein(sprache: KundenSprache): string {
  const { namespace, key } = RETOUREN_LINK_METAFELD;
  const text = sprache === 'de' ? 'Artikel zurückgeben' : 'Return items';
  // Syntax laut Shopify-Hilfe „Notifications variables reference“ (metafields.NAMESPACE.KEY), geprüft am 02.11.2026
  return `{% if metafields.${namespace}.${key} != blank %}\n  <p><a href="{{ metafields.${namespace}.${key} }}">${text}</a></p>\n{% endif %}`;
}

/** E-Mail für die Anzeige kürzen: „er…@beispiel.de“ */
export function emailMaskieren(email: string): string {
  const [n, d] = email.split('@');
  if (!n || !d) return '';
  return `${n.slice(0, Math.min(2, n.length))}…@${d}`;
}
